import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { API, GOOGLE_CLIENT_ID } from "@/lib/constants";
import { JeekotaLogo } from "@/components/shared";
import { Loader2, LinkIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Onboarding Modal - shown to Google-auth users who haven't set a username
const OnboardingModal = () => {
  const { user, setUser, setShowOnboarding } = useAuth();
  const [username, setUsername] = useState("");
  const [link, setLink] = useState("");
  const [bio, setBio] = useState("");
  const [loading, setLoading] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleSubmit = async () => {
    if (!username.trim()) {
      toast.error("Username is required");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/complete-onboarding`, { username, link, bio }, { withCredentials: true });
      if (res.data.success) {
        setUser(res.data.user);
        setShowOnboarding(false);
        toast.success("Profile set up successfully!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (dontShowAgain) {
      try {
        await axios.post(`${API}/auth/complete-onboarding`, { dismiss: true }, { withCredentials: true });
      } catch {}
    }
    setShowOnboarding(false);
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" data-testid="onboarding-modal">
        <DialogHeader>
          <DialogTitle className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-jeekota-primary/10 mb-3 mx-auto">
              <JeekotaLogo size={40} />
            </div>
            <div className="text-xl font-bold text-jeekota-text">Complete Your Profile</div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-jeekota-text-secondary text-center">
            Welcome to Jeekota, {user?.name?.split(' ')[0] || 'there'}! Set up your username so others can find and tag you.
          </p>
          <div>
            <Label className="text-sm font-medium">Username *</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-jeekota-text-secondary font-medium">@</span>
              <Input
                data-testid="onboarding-username-input"
                placeholder="your_username"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                className="h-12 rounded-xl pl-8"
                maxLength={30}
              />
            </div>
            <p className="text-xs text-jeekota-text-secondary mt-1">3-30 characters, letters, numbers, underscores</p>
          </div>
          <div>
            <Label className="text-sm font-medium">Website / Social Link</Label>
            <div className="relative mt-1">
              <LinkIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-jeekota-text-secondary" />
              <Input
                data-testid="onboarding-link-input"
                placeholder="https://your-website.com or social profile"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                className="h-12 rounded-xl pl-10"
              />
            </div>
            <p className="text-xs text-jeekota-text-secondary mt-1">Organization website or social media profile</p>
          </div>
          <div>
            <Label className="text-sm font-medium">Bio</Label>
            <Textarea
              data-testid="onboarding-bio-input"
              placeholder="Tell people a bit about yourself (optional)"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="rounded-xl resize-none mt-1"
              rows={2}
              maxLength={200}
            />
            <p className="text-xs text-jeekota-text-secondary mt-1">{bio.length}/200 characters</p>
          </div>
          <Button
            data-testid="onboarding-submit-btn"
            className="w-full h-12 rounded-xl bg-jeekota-primary hover:bg-jeekota-primary/90"
            onClick={handleSubmit}
            disabled={loading || !username.trim()}
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save & Continue"}
          </Button>
          <div className="flex items-center justify-between pt-2 border-t">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="onboarding-dont-show-label">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="rounded border-gray-300"
                data-testid="onboarding-dont-show-checkbox"
              />
              <span className="text-xs text-jeekota-text-secondary">Don't show again</span>
            </label>
            <button
              data-testid="onboarding-skip-btn"
              onClick={handleDismiss}
              className="text-xs text-jeekota-text-secondary hover:text-jeekota-text underline"
            >
              Skip for now
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      if (response.data?.needs_onboarding) {
        setShowOnboarding(true);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setShowOnboarding(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, showOnboarding, setShowOnboarding }}>
      {children}
      {showOnboarding && user && <OnboardingModal />}
    </AuthContext.Provider>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-jeekota-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-jeekota-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AuthPromptContext = createContext();
export const useAuthPrompt = () => useContext(AuthPromptContext);

const AuthPromptModal = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="auth-prompt-modal">
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-jeekota-primary/10 mb-4">
            <JeekotaLogo size={40} />
          </div>
          <h3 className="text-lg font-bold text-jeekota-text mb-2">Join the Community</h3>
          <p className="text-sm text-jeekota-text-secondary mb-6">
            Sign in to create topics, comment, pledge, and donate.
          </p>
          <div className="space-y-3">
            <Button
              data-testid="auth-prompt-signin"
              className="w-full bg-jeekota-primary hover:bg-jeekota-primary/90"
              onClick={() => { onOpenChange(false); navigate('/login'); }}
            >
              Sign In
            </Button>
            <Button
              data-testid="auth-prompt-signup"
              variant="outline"
              className="w-full border-jeekota-primary text-jeekota-primary hover:bg-jeekota-primary/10"
              onClick={() => { onOpenChange(false); navigate('/login', { state: { signup: true } }); }}
            >
              Create Account
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AuthPromptProvider = ({ children }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const { user } = useAuth();

  const requireAuth = (callback) => {
    if (!user) {
      setShowPrompt(true);
      return false;
    }
    if (user.email_verified === false && !user.auth_provider) {
      toast.error("Please verify your email first. Check your inbox for the verification link.");
      return false;
    }
    if (callback) callback();
    return true;
  };

  return (
    <AuthPromptContext.Provider value={{ requireAuth, showPrompt, setShowPrompt }}>
      {children}
      <AuthPromptModal open={showPrompt} onOpenChange={setShowPrompt} />
    </AuthPromptContext.Provider>
  );
};
